"use client"

import Text from "@/components/common/Typography/Text"
import { EnumHotelSearchType, HotelLocation } from "@/lib/redux/features/hotel/type"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { FC, memo } from "react"
import { generateAddressHotel } from "@/utils/hotel/generateAddressHotel"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import DestinationLocationThumb from "./DestinationLocationThumb"

type SearchingHotelsItemProps = {
  location: HotelLocation
  onChange?: (location: HotelLocation) => void
  labels: Record<string, string>
  icons: Record<string, IconDefinition>
  hotelLabel: string
  nested?: boolean
}

const SearchingHotelsItem: FC<SearchingHotelsItemProps> = ({
  location,
  onChange,
  labels,
  icons,
  hotelLabel,
  nested = false,
}) => {
  const { getTranslatedText } = useTETranslation()
  const searchType = location.searchType || ""
  const title = getTranslatedText(location.nameLang?.vi, location.nameLang?.en)
  const typeLabel = labels[searchType] || searchType
  const hotelCount = location.activeHotel || location.numsHotel || 0
  const subtitle =
    searchType === EnumHotelSearchType.HOTEL
      ? generateAddressHotel(location, getTranslatedText)
      : `${hotelCount.toLocaleString()} ${hotelLabel}`

  return (
    <button
      type="button"
      className={`home__searching--destination-row${nested ? " home__searching--destination-row--nested" : ""}`}
      onClick={() => onChange?.(location)}
    >
      <span className="home__searching--destination-row-thumb">
        {nested ? (
          <DestinationLocationThumb
            imageUrl={location.imageViewUrl}
            icon={icons[searchType] || icons.AREA}
          />
        ) : (
          <span className="home__searching--destination-row-icon-fallback">
            <FontAwesomeIcon icon={icons[searchType] || icons.AREA} aria-hidden />
          </span>
        )}
      </span>
      <span className="home__searching--destination-row-body">
        <Text className="home__searching--destination-row-title">{title}</Text>
        <span className="home__searching--destination-row-meta">
          <Text type="secondary" className="home__searching--destination-row-subtitle">
            {subtitle}
          </Text>
          {typeLabel && (
            <>
              <span className="home__searching--destination-row-dot" aria-hidden>
                ·
              </span>
              <span className="home__searching--destination-row-type">{typeLabel}</span>
            </>
          )}
        </span>
      </span>
    </button>
  )
}

SearchingHotelsItem.displayName = "SearchingHotelsItem"

export default memo(SearchingHotelsItem)
